import React from 'react';
import { useViewedPros, DEFAULT_VIEWED_GROUP } from '../contexts/ViewedProsContext';

/** Highlight colors. `green` is the PRO default; `blue` (light blue) is used for
 *  a second, independently tracked column on the same table — e.g. Ticket # next
 *  to PRO on the TTs & Service Failures grids. */
export type ViewedTone = 'green' | 'blue';

/** Exported so other "you have already clicked this" affordances wear the SAME
 *  pill as the PRO one — e.g. the carrier cells' run-trace control, which marks
 *  itself only when the trace actually fires rather than on any click in the cell,
 *  and so cannot use this component directly. */
export const VIEWED_TONE_CLASSES: Record<ViewedTone, { viewed: string; lastViewed: string }> = {
  green: {
    viewed: 'bg-green-200 dark:bg-green-600/50 px-1 rounded',
    lastViewed: 'ring-2 ring-inset ring-green-600 dark:ring-green-400 rounded',
  },
  blue: {
    viewed: 'bg-sky-200 dark:bg-sky-600/50 px-1 rounded',
    lastViewed: 'ring-2 ring-inset ring-sky-600 dark:ring-sky-400 rounded',
  },
};

interface ProTableCellProps {
  pro: number | string | null | undefined;
  children: React.ReactNode;
  className?: string;
  /** Highlight color. Defaults to green (the PRO highlight). */
  tone?: ViewedTone;
  /** Tracking namespace — values in different groups never cross-highlight and
   *  each group keeps its own last-clicked ring. Defaults to the PRO group. */
  group?: string;
}

/**
 * Pill decorator for a PRO value. Applies a colored background to every instance
 * of a value the user has clicked, plus a matching ring on the single most-recently
 * clicked value so the user can tell which one they opened last (clicking another
 * value moves the ring to it). View state lives in ViewedProsContext (page-wide,
 * shared across tables/cards); no-op when no provider is mounted.
 *
 * TanTable wraps the `trackViewedColumnId` column's cell content in this
 * automatically (and `trackViewedBlueColumnId`'s in the blue tone) — callers
 * rarely render it directly.
 */
export const ProTableCell: React.FC<ProTableCellProps> = ({
  pro,
  children,
  className,
  tone = 'green',
  group = DEFAULT_VIEWED_GROUP,
}) => {
  const { isViewed, isLastViewed, markViewed } = useViewedPros();
  const viewed = isViewed(pro, group);
  const lastViewed = isLastViewed(pro, group);
  const toneClasses = VIEWED_TONE_CLASSES[tone] ?? VIEWED_TONE_CLASSES.green;

  return (
    <span
      // Capture phase so the view is still recorded even when the inner
      // link/button calls stopPropagation() (several PRO cells do). Defer the
      // mark to a later task: marking synchronously here re-renders mid-click,
      // which detaches the click target before the bubble-phase open handler
      // (the row's onClick / the link's onClick) runs — eating the first click
      // so the drawer needed two clicks. Deferring lets the open fire first; the
      // highlight then applies a tick later (imperceptible).
      onClickCapture={() => { setTimeout(() => markViewed(pro, group), 0); }}
      title={lastViewed ? 'Last viewed' : viewed ? 'Viewed' : undefined}
      className={`inline-flex items-center gap-1 transition-colors duration-150 ${
        viewed ? toneClasses.viewed : ''
      } ${
        lastViewed ? toneClasses.lastViewed : ''
      } ${className ?? ''}`}
    >
      {children}
    </span>
  );
};
